import { RouterProvider, createRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { StrictMode, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { routeTree } from './routeTree.gen.ts'
import './index.css'

function RouterErrorFallback({ error }: { error: unknown }) {
  const hasCaptured = useRef(false)

  useEffect(() => {
    if (hasCaptured.current) return
    hasCaptured.current = true
    Sentry.captureException(error, {
      tags: { source: 'tanstack-router' },
    })
  }, [error])

  return (
    <div className="flex min-h-[40vh] items-start justify-center px-6 pt-10">
      <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-5">
        <p className="text-base font-semibold text-[#0B2540]">Something went wrong!</p>
        <p className="mt-2 text-sm text-[#0B2540]/70">
          O erro foi registrado. Tente recarregar a página.
        </p>
      </div>
    </div>
  )
}

const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultErrorComponent: ({ error }) => <RouterErrorFallback error={error} />,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')!
const root = createRoot(rootElement)

Sentry.init({
  dsn: 'https://903972f2236bd5dc4d4632159226a4ca@o4508407707598848.ingest.us.sentry.io/4510841763725313',
  sendDefaultPii: true,
})

window.addEventListener('error', (event) => {
  Sentry.captureException(event.error ?? new Error(event.message), {
    tags: { source: 'window.onerror' },
  })
})

window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason, {
    tags: { source: 'window.unhandledrejection' },
  })
})

const preloadRouteChunks = () => {
  void import('@/features/objective-selector/ObjectiveSelector')
  void import('@/features/welcome/Welcome')
  void import('@/features/chat/Chat')
  void import('@/features/loading/Loading')
  void import('@/features/result/Result')
  void import('@/features/contact/Contact')
  void import('@/features/success/Success')
  void import('@/features/checkout/Checkout')
  void import('@/features/payment/Payment')
  void import('@/features/legal/Legal')
  void import('@/features/protocolo/Protocolo')
  void import('@/features/entrar/Entrar')
  void import('@/features/account/AccountSettings')
  void import('@/features/account/AccountDeletionSuccess')
}

const scheduleIdle = (cb: () => void) => {
  if (typeof window === 'undefined') {
    return
  }

  const windowWithIdle = window as Window & {
    requestIdleCallback?: (callback: () => void) => number
  }

  if (windowWithIdle.requestIdleCallback) {
    windowWithIdle.requestIdleCallback(cb)
  } else {
    setTimeout(cb, 1)
  }
}

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary
      showDialog={false}
      fallback={
        <div className="flex min-h-[40vh] items-start justify-center px-6 pt-10">
          <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-5">
            <p className="text-base font-semibold text-[#0B2540]">Something went wrong!</p>
            <p className="mt-2 text-sm text-[#0B2540]/70">
              O erro foi registrado. Tente recarregar a página.
            </p>
          </div>
        </div>
      }
    >
      <HelmetProvider>
        <RouterProvider router={router} />
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
)

scheduleIdle(preloadRouteChunks)
